import { Component, OnInit } from '@angular/core';
import moment from 'moment';
import { DataService } from '../../../shared/services/data.service';

interface SchedulerPerformanceRow {
  schedulerKey: string;
  schedulerName: string;
  passCount: number;
  failCount: number;
  skipCount: number;
  jobStatus: number;
  statusText: string;
  lastExecutionAt: number;
}

@Component({
  selector: 'wtaf-scheduler-performance-widget',
  templateUrl: './scheduler-performance-widget.component.html',
  styleUrls: ['./scheduler-performance-widget.component.css']
})
export class SchedulerPerformanceWidgetComponent implements OnInit {
  rows: SchedulerPerformanceRow[] = [];
  loading = false;
  errorMessage = '';
  showAll = false;
  readonly displayCount = 5;

  constructor(private dataservice: DataService) {}

  ngOnInit() {
    this.loadSchedulerPerformance();
  }

  get visibleRows(): SchedulerPerformanceRow[] {
    return this.showAll ? this.rows : this.rows.slice(0, this.displayCount);
  }

  get canSeeMore(): boolean {
    return this.rows.length > this.displayCount;
  }

  get totalCount(): number {
    return this.rows.length;
  }

  get runningCount(): number {
    return this.getStatusCount(1);
  }

  get completedCount(): number {
    return this.getStatusCount(2);
  }

  get incompleteCount(): number {
    return this.getStatusCount(3);
  }

  get terminatedCount(): number {
    return this.getStatusCount(4);
  }

  get totalSkippedCount(): number {
    return this.rows.reduce((sum, row) => sum + row.skipCount, 0);
  }

  toggleSeeMore() {
    this.showAll = !this.showAll;
  }

  refreshWidget() {
    this.loadSchedulerPerformance();
  }

  getStatusClass(row: SchedulerPerformanceRow): string {
    if (row.jobStatus === 1) {
      return 'runningBtn';
    }
    if (row.jobStatus === 2) {
      return 'passedBtn';
    }
    if (row.jobStatus === 3) {
      return 'failedBtn';
    }
    if (row.jobStatus === 4) {
      return 'incompleteBtn';
    }

    return 'unknownBtn';
  }

  private loadSchedulerPerformance() {
    const userId = parseInt(localStorage.getItem('userId') || '0', 10);
    const toDate = moment().format('YYYY-MM-DD');
    const fromDate = moment()
      .subtract(1, 'day')
      .format('YYYY-MM-DD');

    if (!userId) {
      this.rows = [];
      this.errorMessage =
        'Unable to load scheduler performance: user not found.';
      return;
    }

    this.loading = true;
    this.errorMessage = '';

    this.dataservice
      .getSchedulerPerformanceByUser(userId, fromDate, toDate, 0, 100)
      .subscribe(
        (response: any) => {
          this.rows = this.normalizeRows(this.extractRecords(response));
          this.showAll = false;
          this.loading = false;
        },
        () => {
          this.rows = [];
          this.loading = false;
          this.errorMessage =
            'Unable to fetch scheduler performance. Please try again.';
        }
      );
  }

  private extractRecords(response: any): any[] {
    if (!response) {
      return [];
    }

    if (Array.isArray(response)) {
      return response;
    }

    if (Array.isArray(response.listOfRecords)) {
      return response.listOfRecords;
    }

    if (Array.isArray(response.records)) {
      return response.records;
    }

    return [];
  }

  private normalizeRows(records: any[]): SchedulerPerformanceRow[] {
    const grouped: { [key: string]: SchedulerPerformanceRow } = {};

    records.forEach(item => {
      const schedulerKey = this.getSchedulerKey(item);
      const row: SchedulerPerformanceRow = {
        schedulerKey,
        schedulerName: this.getSchedulerName(item),
        passCount: this.toNumber(item.passCount),
        failCount: this.toNumber(item.failCount),
        skipCount: this.toNumber(item.skipCount),
        jobStatus: this.toNumber(item.jobStatus),
        statusText: this.getStatusText(item),
        lastExecutionAt: this.getExecutionTime(item)
      };

      if (
        !grouped[schedulerKey] ||
        grouped[schedulerKey].lastExecutionAt < row.lastExecutionAt
      ) {
        grouped[schedulerKey] = row;
      }
    });

    const normalizedRows = Object.keys(grouped).map(key => grouped[key]);
    normalizedRows.sort((a, b) => b.lastExecutionAt - a.lastExecutionAt);

    return normalizedRows;
  }

  private getSchedulerKey(item: any): string {
    if (item.exeHeaderId) {
      return 'exeHeaderId-' + item.exeHeaderId;
    }

    if (item.scheduleId) {
      return 'scheduleId-' + item.scheduleId;
    }

    return 'name-' + this.getSchedulerName(item);
  }

  private getSchedulerName(item: any): string {
    return (
      item.scheduleName ||
      item.schedulerName ||
      item.jobName ||
      item.executionName ||
      item.testExecutionName ||
      'NA'
    );
  }

  private getStatusText(item: any): string {
    if (item.statusText) {
      return item.statusText;
    }

    const status = this.toNumber(item.jobStatus);
    if (status === 1) {
      return 'Running';
    }
    if (status === 2) {
      return 'Completed';
    }
    if (status === 3) {
      return 'Incomplete';
    }
    if (status === 4) {
      return 'Terminated';
    }

    return 'Unknown';
  }

  private getExecutionTime(item: any): number {
    if (item.startDateTime) {
      return moment(item.startDateTime).valueOf();
    }

    if (item.endDateTime) {
      return moment(item.endDateTime).valueOf();
    }

    if (item.startDate && item.startTime) {
      return moment(item.startDate + ' ' + item.startTime).valueOf();
    }

    if (item.endDate && item.endTime) {
      return moment(item.endDate + ' ' + item.endTime).valueOf();
    }

    if (item.exeHistoryId) {
      return this.toNumber(item.exeHistoryId);
    }

    return 0;
  }

  private toNumber(value: any): number {
    const parsedValue = parseInt(value, 10);
    return isNaN(parsedValue) ? 0 : parsedValue;
  }

  private getStatusCount(status: number): number {
    return this.rows.filter(row => row.jobStatus === status).length;
  }
}
